
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, RefreshCw, Download, CheckCircle, AlertCircle, Database, Key, Shield } from 'lucide-react';

interface SyncResult {
  success: boolean;
  syncedCount: number;
  errorCount: number;
  errors: string[];
}

interface FullSyncResult {
  success: boolean;
  message: string;
  totalSynced: number;
  totalErrors: number;
  results: {
    customers: SyncResult;
    estimates: SyncResult;
    invoices: SyncResult;
  };
}

interface CredentialsStatus {
  hasAccessToken: boolean;
  hasClientId: boolean;
  hasClientSecret: boolean;
  connectionStatus: 'unknown' | 'connected' | 'error';
  lastChecked: string;
}

const AdminWaveIntegrationTab: React.FC = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState<FullSyncResult | null>(null);
  const [syncInProgress, setSyncInProgress] = useState<string>('');
  const [credentialsStatus, setCredentialsStatus] = useState<CredentialsStatus | null>(null);
  const [isCheckingCredentials, setIsCheckingCredentials] = useState(false);

  const checkCredentialsStatus = async () => {
    setIsCheckingCredentials(true);
    try {
      const { data, error } = await supabase.functions.invoke('wave-credentials-status');
      
      if (error) throw error;
      
      setCredentialsStatus(data);
    } catch (error) {
      console.error('Failed to check credentials status:', error);
      toast({
        title: 'Failed to check credentials',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setIsCheckingCredentials(false);
    }
  };

  useEffect(() => {
    checkCredentialsStatus();
  }, []);

  const handleFullSync = async () => {
    setIsLoading(true);
    setSyncInProgress('Starting full Wave data sync...');
    
    try {
      const { data, error } = await supabase.functions.invoke('sync-all-wave-data');
      
      if (error) throw error;
      
      setLastSyncResult(data);
      setSyncInProgress('');
      
      toast({
        title: 'Sync Completed',
        description: `${data.totalSynced} items synced, ${data.totalErrors} errors`,
        variant: data.totalErrors > 0 ? 'destructive' : 'default'
      });
    } catch (error) {
      console.error('Full sync failed:', error);
      setSyncInProgress('');
      toast({
        title: 'Sync Failed',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleIndividualSync = async (type: 'customers' | 'estimates' | 'invoices') => {
    setIsLoading(true);
    setSyncInProgress(`Syncing ${type}...`);
    
    try {
      const functionName = `import-wave-${type}`;
      const { data, error } = await supabase.functions.invoke(functionName);
      
      if (error) throw error;
      
      setSyncInProgress('');
      
      toast({
        title: `${type.charAt(0).toUpperCase() + type.slice(1)} Sync Completed`,
        description: `${data.syncedCount} items synced, ${data.errorCount} errors`,
        variant: data.errorCount > 0 ? 'destructive' : 'default'
      });
    } catch (error) {
      console.error(`${type} sync failed:`, error);
      setSyncInProgress('');
      toast({
        title: 'Sync Failed',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getSyncStatusBadge = (result: SyncResult) => {
    if (result.errorCount > 0) {
      return <Badge variant="destructive">Partial Success</Badge>;
    }
    if (result.syncedCount > 0) {
      return <Badge variant="default" className="bg-green-500">Success</Badge>;
    }
    return <Badge variant="secondary">No Data</Badge>;
  };

  const getCredentialBadge = (hasCredential: boolean) => {
    return hasCredential ? (
      <Badge variant="default" className="bg-green-500">
        <CheckCircle className="h-3 w-3 mr-1" />
        Configured
      </Badge>
    ) : (
      <Badge variant="destructive">
        <AlertCircle className="h-3 w-3 mr-1" />
        Missing
      </Badge>
    );
  };

  const getConnectionBadge = (status: string) => {
    switch (status) {
      case 'connected':
        return (
          <Badge variant="default" className="bg-green-500">
            <CheckCircle className="h-3 w-3 mr-1" />
            Connected
          </Badge>
        );
      case 'error':
        return (
          <Badge variant="destructive">
            <AlertCircle className="h-3 w-3 mr-1" />
            Connection Failed
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary">
            <AlertCircle className="h-3 w-3 mr-1" />
            Unknown
          </Badge>
        );
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Wave Integration</h2>
        <p className="text-gray-600">
          Manage Wave accounting integration and sync data between systems.
        </p>
      </div>

      {/* Credentials Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            Wave API Credentials
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {credentialsStatus ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Client ID:</span>
                  {getCredentialBadge(credentialsStatus.hasClientId)}
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium">Client Secret:</span>
                  {getCredentialBadge(credentialsStatus.hasClientSecret)}
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium">Access Token:</span>
                  {getCredentialBadge(credentialsStatus.hasAccessToken)}
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Connection Status:</span>
                  {getConnectionBadge(credentialsStatus.connectionStatus)}
                </div>
                <div className="text-sm text-gray-600">
                  Last checked: {new Date(credentialsStatus.lastChecked).toLocaleString()}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Checking credentials...</span>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={checkCredentialsStatus}
              disabled={isCheckingCredentials}
              size="sm"
            >
              {isCheckingCredentials ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Refresh Status
            </Button>
          </div>

          {credentialsStatus && (!credentialsStatus.hasAccessToken && (!credentialsStatus.hasClientId || !credentialsStatus.hasClientSecret)) && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-yellow-800">Incomplete Credentials</h4>
                  <p className="text-sm text-yellow-700 mt-1">
                    You need either a Wave Access Token OR both Client ID and Client Secret configured to use the Wave integration.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sync Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Data Synchronization
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={handleFullSync}
              disabled={isLoading || (credentialsStatus?.connectionStatus !== 'connected')}
              className="flex items-center gap-2"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Sync All Wave Data
            </Button>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => handleIndividualSync('customers')}
                disabled={isLoading || (credentialsStatus?.connectionStatus !== 'connected')}
                size="sm"
              >
                Sync Customers
              </Button>
              <Button
                variant="outline"
                onClick={() => handleIndividualSync('estimates')}
                disabled={isLoading || (credentialsStatus?.connectionStatus !== 'connected')}
                size="sm"
              >
                Sync Estimates
              </Button>
              <Button
                variant="outline"
                onClick={() => handleIndividualSync('invoices')}
                disabled={isLoading || (credentialsStatus?.connectionStatus !== 'connected')}
                size="sm"
              >
                Sync Invoices
              </Button>
            </div>
          </div>

          {syncInProgress && (
            <div className="flex items-center gap-2 text-sm text-blue-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              {syncInProgress}
            </div>
          )}

          {credentialsStatus?.connectionStatus !== 'connected' && (
            <div className="text-sm text-gray-500">
              Sync operations are disabled until Wave API connection is established.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Last Sync Results */}
      {lastSyncResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              Last Sync Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="font-medium">Overall Status:</span>
                <Badge variant={lastSyncResult.totalErrors > 0 ? "destructive" : "default"}>
                  {lastSyncResult.totalSynced} synced, {lastSyncResult.totalErrors} errors
                </Badge>
              </div>

              <Separator />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Customers:</span>
                    {getSyncStatusBadge(lastSyncResult.results.customers)}
                  </div>
                  <div className="text-sm text-gray-600">
                    {lastSyncResult.results.customers.syncedCount} synced
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Estimates:</span>
                    {getSyncStatusBadge(lastSyncResult.results.estimates)}
                  </div>
                  <div className="text-sm text-gray-600">
                    {lastSyncResult.results.estimates.syncedCount} synced
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Invoices:</span>
                    {getSyncStatusBadge(lastSyncResult.results.invoices)}
                  </div>
                  <div className="text-sm text-gray-600">
                    {lastSyncResult.results.invoices.syncedCount} synced
                  </div>
                </div>
              </div>

              {/* Show errors if any */}
              {lastSyncResult.totalErrors > 0 && (
                <div className="space-y-2">
                  <Separator />
                  <div className="flex items-center gap-2 text-red-600">
                    <AlertCircle className="h-4 w-4" />
                    <span className="font-medium">Sync Errors:</span>
                  </div>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {[
                      ...lastSyncResult.results.customers.errors,
                      ...lastSyncResult.results.estimates.errors,
                      ...lastSyncResult.results.invoices.errors
                    ].map((error, index) => (
                      <div key={index} className="text-sm text-red-600 bg-red-50 p-2 rounded">
                        {error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Wave Connection Status */}
      <Card>
        <CardHeader>
          <CardTitle>Wave Connection Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="font-medium">API Connection</div>
              <div className="text-sm text-gray-600">
                Connected to Wave accounting system
              </div>
            </div>
            <Badge variant="default" className="bg-green-500">
              <CheckCircle className="h-3 w-3 mr-1" />
              Connected
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Integration Info */}
      <Card>
        <CardHeader>
          <CardTitle>Integration Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="font-medium mb-1">Sync Direction:</div>
              <div className="text-gray-600">Bidirectional (Local ↔ Wave)</div>
            </div>
            <div>
              <div className="font-medium mb-1">Data Types:</div>
              <div className="text-gray-600">Customers, Estimates, Invoices</div>
            </div>
            <div>
              <div className="font-medium mb-1">Auto Sync:</div>
              <div className="text-gray-600">Manual trigger only</div>
            </div>
            <div>
              <div className="font-medium mb-1">Conflict Resolution:</div>
              <div className="text-gray-600">Wave data takes precedence</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminWaveIntegrationTab;
